import { AddTagRequest, CreatePointRequest, CreatePointValidationError, CreatePointsResponse, GetPointResponse, GetTopicResponse, HOTRow, TopicCategory } from '../../types';
import { makeId, undefinedIfEmpty } from '../utils';

import { escapeCsvValue } from '../files';
import { getMainPage } from '../page-name';

export function createDummyPoint(topic: GetTopicResponse): HOTRow {
    return {
        _id: { $oid: `dummy${makeId()}` },
        email: undefined,
        topic_id: topic._id,
        topic_category_id: undefined,
        location: undefined,
        name: undefined,
        description: undefined,
        start: undefined,
        end: undefined,
        url: undefined,
        tag_info: {
            google_tags: [],
            user_tags: [],
        },
        special_offer: undefined,
        created_at: undefined,
        updated_at: undefined,
        is_new: undefined,
        embedded_video: undefined,
        custom_icon_url: undefined,
        active: undefined,
        is_gate_hidden: undefined,
        source: undefined,
        featured: undefined,
        custom_information: undefined,
        number: undefined,
        url_images: undefined,
        custom_shape: undefined,
    };
}

export function pointRequestFromHOTRow(p: HOTRow): CreatePointRequest {
    if (p.topic_category_id === undefined) {
        getMainPage().errorToastElement.showToastAndThrow(`Error on ${p.name ?? p.location?.search ?? 'row'}: Category Invalid.`);
        throw new Error('Category Invalid.');
    }
    if (p.location === undefined) {
        getMainPage().errorToastElement.showToastAndThrow(`Error on ${p.name ?? 'row'}: Location Invalid.`);
        throw new Error('No location in point to be added to the request.');
    }

    const tag_info: AddTagRequest = {
        user_tags: p.tag_info?.user_tags ?? [],
    };

    return {
        _id: p._id,
        email: p.email,
        topic_id: p.topic_id,
        topic_category_id: p.topic_category_id,
        location: p.location,
        name: p.name,
        description: p.description,
        special_offer: p.special_offer,
        url: p.url,
        active: p.active,
        featured: p.featured,
        start: p.start,
        end: p.end,
        number: undefinedIfEmpty(p.number),
        source: p.source,
        tag_info,
        embedded_video: p.embedded_video,
        custom_icon_url: p.custom_icon_url,
        is_gate_hidden: p.is_gate_hidden,
        custom_information: p.custom_information,
        custom_shape: p.custom_shape,
        url_images: p.url_images
    };
}

export function createHOTRowsFromPoints(points: GetPointResponse[]): HOTRow[] {
    const rows = [];
    for (let i = 0; i < points.length; i++) {
        rows.push({
            _id: { $oid: points[i]._id.$oid },
            email: points[i].email,
            topic_id: points[i].topic_id,
            topic_category_id: points[i].topic_category_id,
            location: points[i].location,
            name: points[i].name,
            description: points[i].description,
            start: points[i].start,
            end: points[i].end,
            number: points[i].number,
            url: points[i].url,
            tag_info: points[i].tag_info,
            special_offer: points[i].special_offer,
            opentable_info: points[i].opentable_info,
            created_at: points[i].created_at,
            updated_at: points[i].updated_at,
            is_new: undefined,
            embedded_video: points[i].embedded_video,
            custom_icon_url: points[i].custom_icon_url,
            active: points[i].active,
            is_gate_hidden: points[i].is_gate_hidden,
            source: points[i].source,
            featured: points[i].featured,
            custom_information: points[i].custom_information,
            custom_shape: points[i].custom_shape,
            url_images: undefined,
        });
    }
    return rows;
}

// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
export function createErrorStringForBulk(responses: Array<CreatePointsResponse | undefined | void>, topic: GetTopicResponse | undefined, categoryIdMap: Map<string, TopicCategory>, deleteResults?: Array<{ _id: string, error: string }> | undefined) {
    let failed_geocodes: CreatePointRequest[] = [];
    let failed_validations: CreatePointValidationError[] = [];
    for (let i = 0; i < responses.length; i++) {
        const currentResponse = responses[i];
        if (currentResponse !== undefined) {
            failed_geocodes = failed_geocodes.concat(currentResponse.failed_geocodes);
            failed_validations = failed_validations.concat(currentResponse.failed_validations);
        }
    }

    if (failed_geocodes.length === 0 && failed_validations.length === 0) {
        return;
    }

    // Headers
    let errorString = '"Error Type","Error Message","Location","Category"';
    if (topic?.enable_point_numbering === true) {
        errorString += ',"Point Number"';
    }
    errorString += ',"Title","Description","Tags","URL","Special Offer"';
    if (topic?.has_dates === true) {
        errorString += ',"Start","End"';
    }

    // Geocode errors
    for (let i = 0; i < failed_geocodes.length; i++) {
        errorString += `\nLocation failed to geocode (try adding a street name), it was a custom shape, or there were too many options in the area. Could not geocode Location field,"${failed_geocodes[i]?.location?.search ?? ''}","${categoryIdMap.get(failed_geocodes[i].topic_category_id?.$oid)?.name ?? ''}",`;
        if (topic?.enable_point_numbering === true) {
            errorString += `"${failed_geocodes[i].number ?? ''}",`;
        }
        errorString += `"${failed_geocodes[i].name ?? ''}",${escapeCsvValue(failed_geocodes[i].description)},"${failed_geocodes[i].tag_info.user_tags.map(tag => tag._id.$oid).join(',')}","${failed_geocodes[i].url ?? ''}","${failed_geocodes[i].special_offer?.description ?? ''}"`;
        if (topic?.has_dates === true) {
            errorString += `,"${failed_geocodes[i].start ?? ''}","${failed_geocodes[i].end ?? ''}"`;
        }
    }

    for (let i = 0; i < failed_validations.length; i++) {
        errorString += `\nPoint failed validation,"${failed_validations[i].message}","${failed_geocodes[i]?.location?.search ?? ''}","${categoryIdMap.get(failed_geocodes[i].topic_category_id?.$oid)?.name ?? ''}",`;
        if (topic?.enable_point_numbering === true) {
            errorString += `"${failed_geocodes[i].number ?? ''}",`;
        }
        errorString += `"${failed_geocodes[i].name ?? ''}",${escapeCsvValue(failed_geocodes[i].description)},"${failed_geocodes[i].tag_info.user_tags.map(tag => tag._id.$oid).join(',')}","${failed_geocodes[i].url ?? ''}","${failed_geocodes[i].special_offer?.description ?? ''}"`;
        if (topic?.has_dates === true) {
            errorString += `,"${failed_geocodes[i].start ?? ''}","${failed_geocodes[i].end ?? ''}"`;
        }
    }

    // Delete errors
    if (deleteResults !== undefined) {
        for (let i = 0; i < deleteResults.length; i++) {
            errorString += `\nDelete failed,"${deleteResults[i].error}","${deleteResults[i]._id}"}"`;
        }
    }

    return errorString;
}
